import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";

interface AddToLayoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layoutNames: string[];
  containerCount: number;
  onConfirm: (layoutName: string) => Promise<void> | void;
}

export const AddToLayoutDialog = ({
  open,
  onOpenChange,
  layoutNames,
  containerCount,
  onConfirm,
}: AddToLayoutDialogProps) => {
  const [selectedLayout, setSelectedLayout] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenChange = (next: boolean) => {
    if (!next) setSelectedLayout("");
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!selectedLayout) return;
    setIsSubmitting(true);
    try {
      await onConfirm(selectedLayout);
      handleOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add to Layout</DialogTitle>
          <DialogDescription>
            {`Add ${containerCount} selected container${containerCount === 1 ? "" : "s"} to a layout. Containers already in the chosen layout are skipped.`}
          </DialogDescription>
        </DialogHeader>
        <Select value={selectedLayout} onValueChange={setSelectedLayout}>
          <SelectTrigger className="text-primary">
            <SelectValue placeholder="Select a layout" />
          </SelectTrigger>
          <SelectContent>
            {layoutNames.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button
            onClick={() => handleOpenChange(false)}
            variant={"destructive"}
            type={"button"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant={"accent"}
            disabled={!selectedLayout || isSubmitting}
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
